import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise, STRIPE_ELEMENTS_OPTIONS } from '../lib/stripe';
import { PaymentForm } from '../components/PaymentForm';
import { DigitalWalletButton } from '../components/DigitalWalletButton';
import { getUserProfile, updateUserProfile } from '../utils/userApi';
import { validateDiscountCode, calculateDiscount } from '../utils/discountApi';
import { BackButton } from '../components/ui';
import { dollarsToCents } from '../lib/stripe';

export default function Checkout() {
  const [fulfillment, setFulfillment] = useState('pickup'); // Default to pickup
  const [address, setAddress] = useState('');
  const [note, setNote] = useState('');
  const [discount, setDiscount] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [taxInfo, setTaxInfo] = useState(null); // Will store Stripe tax calculation
  const [taxCalculationIntent, setTaxCalculationIntent] = useState(null); // Store the payment intent used for tax calculation

  // Get real cart items from context and user auth
  const { cartItems } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // State for shop data
  const [shopData, setShopData] = useState(null);
  const [shopLoading, setShopLoading] = useState(false);

  // Load user profile and auto-fill address
  useEffect(() => {
    const loadUserProfile = async () => {
      if (currentUser) {
        try {
          const profile = await getUserProfile(currentUser.uid);
          setUserProfile(profile);
          
          // Auto-fill delivery address from user profile
          if (profile?.address) {
            const { street, apt, city, zip } = profile.address;
            const fullAddress = [street, apt, city, zip].filter(Boolean).join(', ');
            setAddress(fullAddress || '');
          }
          
          // Auto-expand address editing if no address exists and delivery is selected
          if (!profile?.address?.street && fulfillment === 'deliver') {
            setIsEditingAddress(true);
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
        }
      }
    };

    loadUserProfile();
  }, [currentUser, fulfillment]);

  // Load shop data to determine available fulfillment options
  useEffect(() => {
    const loadShopData = async () => {
      if (!cartItems.length) {
        console.log('[Checkout] No cart items, skipping shop data load');
        return;
      }
      
      // Get seller ID from first cart item (assuming single seller for now)
      let sellerId = cartItems[0]?.sellerId;
      console.log('[Checkout] Cart items:', cartItems);
      console.log('[Checkout] First cart item:', cartItems[0]);
      console.log('[Checkout] Seller ID from cart:', sellerId);
      
      // If sellerId is missing, try to fetch it from the product document
      if (!sellerId && (cartItems[0]?.productId || cartItems[0]?.id)) {
        console.log('[Checkout] SellerId missing, fetching from product document...');
        try {
          const productId = cartItems[0]?.productId || cartItems[0]?.id;
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('../firebase');
          const productDoc = await getDoc(doc(db, 'products', productId));
          if (productDoc.exists()) {
            sellerId = productDoc.data().sellerId;
            console.log('[Checkout] Fetched sellerId from product:', sellerId);
          }
        } catch (error) {
          console.error('[Checkout] Error fetching product for sellerId:', error);
        }
      }
      
      if (!sellerId) {
        console.warn('[Checkout] No sellerId found in cart item or product, cannot load shop data');
        return;
      }
      
      setShopLoading(true);
      console.log('[Checkout] Loading shop data for sellerId:', sellerId);
      try {
        // Try to get shop data from sellerApi first
        const { getShopProfile } = await import('../utils/sellerApi');
        const shop = await getShopProfile(sellerId);
        console.log('[Checkout] Shop data loaded:', shop);
        console.log('[Checkout] Shop address data:', {
          address: shop?.address,
          state: shop?.address?.state || shop?.state || shop?.storeState || shop?.shopState,
          city: shop?.address?.city || shop?.city || shop?.storeCity,
          zip: shop?.address?.zip || shop?.zip || shop?.storeZip,
          allKeys: shop ? Object.keys(shop) : []
        });
        if (!shop) {
          console.warn('[Checkout] No shop found for sellerId:', sellerId);
        }
        setShopData(shop);
        
        // Set default fulfillment based on what's available
        if (shop) {
          console.log('[Checkout] Shop acceptsDelivery:', shop.acceptsDelivery);
          console.log('[Checkout] Shop acceptsPickup:', shop.acceptsPickup);
          
          if (shop.acceptsDelivery === true && shop.acceptsPickup !== true) {
            // Only delivery available
            setFulfillment('deliver');
          } else if (shop.acceptsPickup === true && shop.acceptsDelivery !== true) {
            // Only pickup available
            setFulfillment('pickup');
          } else if (shop.acceptsPickup === true && shop.acceptsDelivery === true) {
            // Both available, default to pickup
            setFulfillment('pickup');
          } else {
            // If neither is explicitly set to true, assume both are available
            console.log('[Checkout] No fulfillment options explicitly set, assuming both available');
            setFulfillment('pickup');
          }
        } else {
          // If no shop data found, default to pickup
          console.log('[Checkout] No shop data found, defaulting to pickup');
          setFulfillment('pickup');
        }
      } catch (error) {
        console.error('Error loading shop data:', error);
        // Default to pickup if shop data fails to load
        setFulfillment('pickup');
      } finally {
        setShopLoading(false);
      }
    };

    loadShopData();
  }, [cartItems]);

  // Calculate totals (needed for tax calculation)
  const subtotal = cartItems.reduce((sum, item) => {
    const price = Number(item.price) || 0;
    const qty = Number(item.qty) || 0;
    return sum + price * qty;
  }, 0);
  const serviceFee = subtotal > 0 ? 2.0 : 0;
  
  // Fallback tax calculation (8% like cart page) while waiting for accurate calculation
  const fallbackTax = subtotal > 0 ? +(subtotal * 0.08).toFixed(2) : 0;

  // Function to calculate tax when address or fulfillment changes
  const calculateTax = useCallback(async () => {
    if (!cartItems.length || !shopData) {
      // If no shop data yet, set tax to 0 to avoid "Calculating..." forever
      setTaxInfo({ amount: 0, status: 'pending' });
      return;
    }
    
      console.log('[Checkout] calculateTax called', { 
        cartItems: cartItems.length, 
        shopData: !!shopData, 
        fulfillment, 
        subtotal, 
        serviceFee,
        shopDataKeys: shopData ? Object.keys(shopData) : [],
        shopAddress: shopData?.address,
        shopState: shopData?.address?.state || shopData?.state || shopData?.storeState || shopData?.shopState
      });
    
    try {
      // Create a temporary payment intent to get tax calculation
      const sellerAddress = shopData ? {
        street: shopData.address?.street || shopData.street || shopData.storeAddress || '',
        city: shopData.address?.city || shopData.city || shopData.storeCity || '',
        // Try multiple fields; backend also normalizes full names
        state: shopData.address?.state || shopData.state || shopData.storeState || shopData.shopState || '',
        zip: shopData.address?.zip || shopData.zip || shopData.postalCode || shopData.storeZip || '',
        country: 'US'
      } : null;
      
      console.log('[Checkout] Seller address for tax calculation:', sellerAddress);
      
      const tempOrderData = {
        items: cartItems,
        subtotal,
        serviceFee,
        tax: 0,
        discount: discount?.amount || 0,
        total: subtotal + serviceFee,
        fulfillment,
        address: fulfillment === 'deliver' ? address : null,
        note,
        sellerAddress
      };

      // Import payment service
      const { paymentService } = await import('../lib/paymentService');
      
      // Create payment intent to get tax calculation (Stripe expects amount in cents)
      // Use the final total that includes discount to ensure amount matches
      const amountForTaxCalc = subtotal + serviceFee - (discount?.amount || 0);
      const response = await paymentService.createPaymentIntent({
        ...tempOrderData,
        amount: dollarsToCents(amountForTaxCalc),
        currency: 'usd',
      });

      console.log('[Checkout] Payment intent response:', response);

      // Response structure: { success: true, paymentIntent: { tax: {...} } }
      if (response && response.paymentIntent) {
        // Store the payment intent so PaymentForm can reuse it
        setTaxCalculationIntent(response.paymentIntent);
        
        if (response.paymentIntent.tax) {
          const tax = response.paymentIntent.tax;
          console.log('[Checkout] Tax calculated:', tax);
          setTaxInfo({
            amount: tax.amount || 0, // Already in dollars from our backend
            jurisdiction: tax.jurisdiction,
            rate: tax.rate
          });
        } else {
          // Ensure UI does not stay in "Calculating..."
          console.log('[Checkout] No tax found in response, setting to 0');
          setTaxInfo({ amount: 0, status: 'no_tax' });
        }
      } else {
        // Ensure UI does not stay in "Calculating..."
        console.log('[Checkout] No tax found in response, setting to 0');
        setTaxInfo({ amount: 0, status: 'no_tax' });
      }
    } catch (error) {
      console.error('[Checkout] Tax calculation error:', error);
      // Fallback to 0 tax if calculation fails
      setTaxInfo({ amount: 0, status: 'failed' });
    }
  }, [cartItems, shopData, fulfillment, address, note, discount, subtotal, serviceFee]);

  // Calculate tax when fulfillment or address changes
  useEffect(() => {
    console.log('[Checkout] Tax calculation useEffect triggered:', {
      cartItemsLength: cartItems.length,
      hasShopData: !!shopData,
      shopLoading
    });
    
    if (cartItems.length && shopData) {
      console.log('[Checkout] Calling calculateTax...');
      calculateTax();
    } else if (cartItems.length && !shopData && !shopLoading) {
      // If cart has items but shop data hasn't loaded yet and we're not loading, set pending state
      console.log('[Checkout] No shop data available, setting tax to pending');
      setTaxInfo({ amount: 0, status: 'pending' });
    }
  }, [calculateTax, cartItems, shopData, shopLoading]);

  // Save updated address to user profile
  const saveAddress = async () => {
    if (!currentUser || !userProfile) return;
    
    try {
      await updateUserProfile(currentUser.uid, {
        address: userProfile.address
      });
      setIsEditingAddress(false);
      // Show success message or update UI
    } catch (error) {
      console.error('Error saving address:', error);
      setError('Failed to save address. Please try again.');
    }
  };

  // Validate and apply discount code
  const handleApplyDiscount = async () => {
    if (!discountCode.trim() || !cartItems.length) return;
    
    setDiscountLoading(true);
    setDiscountError('');
    
    try {
      // Get the seller ID from the first cart item (assuming single seller for now)
      const sellerId = cartItems[0]?.sellerId;
      if (!sellerId) {
        setDiscountError('Unable to determine seller for discount validation');
        return;
      }
      
      // Get product IDs and category IDs from cart items for scope validation
      const productIds = cartItems.map(item => item.id);
      const categoryIds = cartItems.map(item => item.category).filter(Boolean);
      
      // Validate the discount code
      const validationResult = await validateDiscountCode(
        sellerId, 
        discountCode.trim(), 
        subtotal, 
        productIds, 
        categoryIds
      );
      
      if (validationResult.valid) {
        const discountData = validationResult.discount;
        const discountCalculation = calculateDiscount(discountData, subtotal);
        
        setDiscount({
          code: discountData.code,
          type: discountData.type,
          amount: discountCalculation.discountAmount,
          originalAmount: discountData.amount,
          minSubtotal: discountData.minSubtotal,
          scope: discountData.scope
        });
        
        setDiscountCode('');
        setShowDiscountForm(false);
        setDiscountError('');
      } else {
        setDiscountError(validationResult.error);
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      setDiscountError('Failed to validate discount code. Please try again.');
    } finally {
      setDiscountLoading(false);
    }
  };

  // Remove applied discount
  const handleRemoveDiscount = () => {
    setDiscount(null);
    setDiscountError('');
  };

  // Tax will be calculated by Stripe Tax - we'll get this from the payment intent response
  // subtotal and serviceFee are already calculated above
  const total = subtotal + serviceFee + (taxInfo?.amount || 0) - (discount?.amount || 0);
  
  console.log('[Checkout] Current fulfillment:', fulfillment);
  console.log('[Checkout] Service fee:', serviceFee);

  // Prepare order data for payment
  const orderData = {
    items: cartItems,
    subtotal,
    serviceFee,
    tax: taxInfo?.amount || 0, // Use Stripe tax amount if available
    discount: discount?.amount || 0,
    total: subtotal + serviceFee + (taxInfo?.amount || 0) - (discount?.amount || 0),
    fulfillment,
    address: fulfillment === 'deliver' ? address : null,
    note,
    // Include seller address for pickup tax calculation
    sellerAddress: shopData ? {
      street: shopData.address?.street || '',
      city: shopData.address?.city || '',
      state: shopData.address?.state || '',
      zip: shopData.address?.zip || '',
      country: 'US'
    } : null
  };

  // Handle payment success
  const handlePaymentSuccess = (orderId) => {
    console.log('[Checkout] Payment successful, navigating to order confirmation:', orderId);
    try {
      navigate(`/order-confirmation/${orderId}`);
    } catch (navError) {
      console.error('[Checkout] Navigation failed after payment success:', navError);
      setError('Payment successful but navigation failed. Please check your orders.');
    }
  };

  // Handle payment error
  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
  };

  // Handle checkout initiation
  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      setError('Your cart is empty');
      return;
    }
    
    // Don't block checkout while loading shop data
    if (shopLoading) {
      setError('Please wait while we load your order details');
      return;
    }
    
    // Check if fulfillment options are available
    const hasDelivery = shopData?.acceptsDelivery === true;
    const hasPickup = shopData?.acceptsPickup === true || shopData?.acceptsPickup === undefined;
    
    if (!hasDelivery && !hasPickup) {
      setError('No fulfillment options available for this order');
      return;
    }

    // Ensure tax intent exists before showing the payment form
    try {
      setLoading(true);
      await calculateTax();
    } catch (e) {
      // noop - calculateTax already logs and sets fallback state
    } finally {
      setLoading(false);
    }
    
    setShowPaymentForm(true);
  };

  // Handle back to cart
  const handleBackToCart = () => {
    navigate('/cart');
  };

  // Handle fulfillment change
  const handleFulfillmentChange = (newFulfillment) => {
    setFulfillment(newFulfillment);
    if (newFulfillment === 'pickup') {
      setAddress('');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center py-8 font-montserrat px-4">


      <main className="w-full max-w-md bg-white rounded-xl shadow-md p-6 space-y-8">
        {/* Back Button */}
        <div className="flex justify-start">
          <BackButton 
            onClick={handleBackToCart}
            variant="standard"
            ariaLabel="Back to cart"
          />
        </div>
        
        {/* Fulfillment Toggle */}
        {shopLoading ? (
          <section className="flex gap-4">
            <div className="flex-1 py-2 rounded-lg font-semibold text-neutral-400 bg-neutral-100">
              Loading...
            </div>
          </section>
        ) : (
          <section className="flex gap-4">
            {shopData?.acceptsDelivery === true && (
              <button
                className={`flex-1 py-2 rounded-lg font-semibold transition-colors ${fulfillment === 'deliver' ? 'text-[#C97B53]' : 'text-neutral-500'}`}
                onClick={() => handleFulfillmentChange('deliver')}
              >
                Deliver
              </button>
            )}
            {(shopData?.acceptsPickup === true || shopData?.acceptsPickup === undefined) && (
              <button
                className={`flex-1 py-2 rounded-lg font-semibold transition-colors ${fulfillment === 'pickup' ? 'text-[#C97B53]' : 'text-neutral-500'}`}
                onClick={() => handleFulfillmentChange('pickup')}
              >
                Pick Up
              </button>
            )}

          </section>
        )}

        {/* Address Block */}
        {fulfillment === 'deliver' && (
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm">Delivery Address</div>
                {!isEditingAddress ? (
                  <div className="text-sm text-neutral-600">
                    {address || 'No address set'}
                  </div>
                ) : (
                  <div className="space-y-2 mt-2">
                    <input
                      className="w-full border rounded px-3 py-2 text-sm"
                      placeholder="Street Address"
                      value={userProfile?.address?.street || ''}
                      onChange={(e) => {
                        setUserProfile(prev => ({
                          ...prev,
                          address: { ...prev?.address, street: e.target.value }
                        }));
                        // Update the formatted address
                        const { apt, city, zip } = userProfile?.address || {};
                        setAddress([e.target.value, apt, city, zip].filter(Boolean).join(', '));
                      }}
                    />
                    <input
                      className="w-full border rounded px-3 py-2 text-sm"
                      placeholder="Apartment, suite, etc. (optional)"
                      value={userProfile?.address?.apt || ''}
                      onChange={(e) => {
                        setUserProfile(prev => ({
                          ...prev,
                          address: { ...prev?.address, apt: e.target.value }
                        }));
                        const { street, city, zip } = userProfile?.address || {};
                        setAddress([street, e.target.value, city, zip].filter(Boolean).join(', '));
                      }}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        className="border rounded px-3 py-2 text-sm"
                        placeholder="City"
                        value={userProfile?.address?.city || ''}
                        onChange={(e) => {
                          setUserProfile(prev => ({
                            ...prev,
                            address: { ...prev?.address, city: e.target.value }
                          }));
                          const { street, apt, zip } = userProfile?.address || {};
                          setAddress([street, apt, e.target.value, zip].filter(Boolean).join(', '));
                        }}
                      />
                      <input
                        className="border rounded px-3 py-2 text-sm"
                        placeholder="ZIP Code"
                        value={userProfile?.address?.zip || ''}
                        onChange={(e) => {
                          setUserProfile(prev => ({
                            ...prev,
                            address: { ...prev?.address, zip: e.target.value }
                          }));
                          const { street, apt, city } = userProfile?.address || {};
                          setAddress([street, apt, city, e.target.value].filter(Boolean).join(', '));
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
              <button 
                className="text-[#C97B53] font-semibold ml-4 text-sm"
                onClick={() => {
                  if (isEditingAddress) {
                    saveAddress();
                  } else {
                    setIsEditingAddress(true);
                  }
                }}
              >
                {isEditingAddress ? 'Save' : 'Edit Address'}
              </button>
            </div>
            <input
              className="w-full border rounded px-3 py-2 mt-2"
              placeholder="Add note for creator (optional)"
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </section>
        )}

        {/* Discount Section */}
        <section className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <div className="font-semibold">Discount</div>
            {!discount ? (
              <button 
                className="text-[#C97B53] font-semibold text-sm hover:underline"
                onClick={() => setShowDiscountForm(true)}
              >
                Apply Discount
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-green-600 text-sm">
                  Applied: {discount.code} ({discount.type === 'percentage' ? `${discount.originalAmount}%` : `$${discount.originalAmount}`} off)
                </span>
                <button 
                  className="text-red-500 text-sm hover:underline"
                  onClick={handleRemoveDiscount}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
          
          {/* Discount Form */}
          {showDiscountForm && !discount && (
            <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter discount code"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  className="flex-1 border rounded px-3 py-2 text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && handleApplyDiscount()}
                />
                <button
                  onClick={handleApplyDiscount}
                  disabled={discountLoading || !discountCode.trim()}
                  className="px-4 py-2 bg-[#C97B53] text-white rounded text-sm font-semibold hover:bg-[#B86A42] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {discountLoading ? 'Applying...' : 'Apply'}
                </button>

              </div>
              
              {/* Discount Error */}
              {discountError && (
                <div className="text-red-500 text-sm">
                  {discountError}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Payment Summary */}
        <section className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Service Fee</span>
            <span>{serviceFee > 0 ? `$${serviceFee.toFixed(2)}` : 'Free'}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Tax</span>
            <span>
              {taxInfo ? `$${taxInfo.amount.toFixed(2)}` : `$${fallbackTax.toFixed(2)}`}
            </span>
          </div>
          {discount && (
            <div className="flex justify-between text-green-600 text-sm">
              <span>Discount</span>
              <span>- ${discount.amount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-base border-t pt-2">
            <span>Total</span>
            <span>
              ${(subtotal + serviceFee + (taxInfo?.amount || fallbackTax) - (discount?.amount || 0)).toFixed(2)}
            </span>
          </div>
        </section>

        {/* Error Message */}
        {error && (
          <div className="text-red-500 text-center font-semibold p-3 bg-red-50 rounded-md">
            {error}
          </div>
        )}

        {/* Payment Section */}
        {!showPaymentForm ? (
          <button
            className="w-full py-4 rounded-lg bg-gradient-to-r from-[#C97B53] to-[#E89B5A] text-white font-bold text-sm mt-4 hover:from-[#B86A42] hover:to-[#D88A4A] transition-colors"
            onClick={handleCheckout}
            disabled={cartItems.length === 0}
          >
            {cartItems.length === 0 ? 'Cart is Empty' : 'Proceed to Payment'}
          </button>
        ) : (
          <div className="space-y-6">
            <div className="text-left">
              <h2 className="text-lg font-semibold text-gray-900">Payment Information</h2>
            </div>

            {/* Stripe Elements Provider */}
            <Elements stripe={stripePromise} options={STRIPE_ELEMENTS_OPTIONS}>
              {/* Digital Wallet Button */}
              <DigitalWalletButton
                orderData={orderData}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
              />

              {/* Payment Form */}
              {taxCalculationIntent ? (
                <PaymentForm
                  orderData={orderData}
                  userProfile={userProfile}
                  existingPaymentIntent={taxCalculationIntent}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                />
              ) : (
                <div className="py-6 text-sm text-gray-600">Initializing secure payment…</div>
              )}
            </Elements>


          </div>
        )}
      </main>
    </div>
  );
} 